import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { FileText, Loader2 } from 'lucide-react';
import { sanitizeHTML } from '@/utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PolicyPage() {
  const { type } = useParams();
  const location = useLocation();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  // Map URL paths to page IDs
  const getPageId = () => {
    const path = location.pathname;
    if (path.includes('privacy')) return 'privacy';
    if (path.includes('refund')) return 'refund';
    if (path.includes('terms')) return 'terms';
    return type || 'privacy';
  };

  const pageTitles = {
    'privacy': 'Privacy Policy',
    'refund': 'Refund & Cancellation Policy',
    'terms': 'Terms and Conditions'
  };

  const pageId = getPageId();

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/cms/sections/${pageId}`);
        if (res.ok) {
          setSections(await res.json());
        }
      } catch (error) {
        console.error('Error fetching policy content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [pageId]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              {pageTitles[pageId] || 'Policy'}
            </h1>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : sections.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>Policy content not available yet.</p>
              </div>
            ) : (
              sections.map((section) => (
                <div key={section.section_id} className="policy-content">
                  {section.content && (
                    <div 
                      className="prose prose-blue max-w-none
                        prose-headings:text-gray-900 
                        prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4
                        prose-h3:text-xl prose-h3:font-semibold prose-h3:mt-6 prose-h3:mb-3
                        prose-h4:text-lg prose-h4:font-medium prose-h4:mt-4 prose-h4:mb-2
                        prose-p:text-gray-600 prose-p:leading-relaxed
                        prose-ul:text-gray-600 prose-ul:space-y-2
                        prose-li:text-gray-600
                        prose-strong:text-gray-900"
                      dangerouslySetInnerHTML={{ __html: sanitizeHTML(section.content) }}
                    />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Last Updated */}
          <p className="text-center text-sm text-gray-500 mt-6">
            Last updated: January 2026
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default PolicyPage;
